/*
 * Klaviyo restock "Notify me" capture for purchase-paused products.
 *
 * The site's kill switch (custom.purchase_paused) disables buying but leaves
 * stock untouched, so Klaviyo's inventory-driven back-in-stock can't notify
 * these shoppers. Instead every paused surface renders a "Notify me" button
 * (snippets/klaviyo-restock-button.liquid); this module opens a shared modal to
 * collect an email, then makes two public, client-side Klaviyo calls:
 *   1. Create Client Subscription  -> subscribe the email to the Restock
 *      Interest list (marketing consent, so a campaign can reach them).
 *   2. Create Client Event         -> log a "Requested Restock" event tagged
 *      with the product, so the merchant can segment + campaign per product.
 * The merchant sends that campaign manually when purchasing reopens.
 *
 * The Boost-rebuilt collection/search grid has no server HTML, so its paused
 * buy buttons (marked data-purchase-paused by iw-purchase-paused.js) are
 * upgraded into notify triggers here too.
 *
 * Config is seeded from theme settings into window.iwKlaviyoRestock. The Klaviyo
 * site id is read from the already-loaded onsite script when not set explicitly.
 */
(function () {
  'use strict';

  if (window.iwKlaviyoRestockInit) return;
  window.iwKlaviyoRestockInit = true;

  var config = window.iwKlaviyoRestock || {};
  var LIST_ID = config.listId || '';
  var METRIC = config.metric || 'Requested Restock Notification';
  var SOURCE = config.source || 'Purchase Paused Waitlist';
  var LABEL_SHORT = config.labelShort || 'Notify Me When Available';
  var SUBSCRIBED_LABEL = "You're on the waitlist";
  var API_REVISION = '2024-10-15';
  var STORAGE_KEY = 'iwKlaviyoRestockSubscribed';
  var STORAGE_TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 days

  if (!LIST_ID) return;

  var modal = document.querySelector('[data-klaviyo-restock-modal]');
  if (!modal) return;

  var form = modal.querySelector('.js-klaviyo-restock-form');
  var emailInput = modal.querySelector('.js-klaviyo-restock-email');
  var consentInput = modal.querySelector('.js-klaviyo-restock-consent');
  var submitBtn = modal.querySelector('.js-klaviyo-restock-submit');
  var productSlot = modal.querySelector('[data-klaviyo-restock-product]');
  var messageEl = modal.querySelector('[data-klaviyo-restock-message]');

  var activeProduct = null;
  var lastFocused = null;

  /* ---- Klaviyo site id ---- */

  // The app-embedded onsite script carries the public site id (company_id) either
  // as a query param or a path segment; read it from there so the merchant never
  // has to copy it. An explicit setting, when present, wins.
  function resolveSiteId() {
    if (config.siteId) return config.siteId;
    var scripts = document.querySelectorAll('script[src*="klaviyo.com"]');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].getAttribute('src') || '';
      var query = src.match(/[?&]company_id=([A-Za-z0-9]+)/);
      if (query) return query[1];
      var path = src.match(/\/onsite\/js\/([A-Za-z0-9]+)\//);
      if (path) return path[1];
    }
    return '';
  }

  var SITE_ID = resolveSiteId();

  /* ---- Already-subscribed products ---- */

  // Persisted so a shopper who has signed up for a product can't reopen the modal
  // for it again — on the PDP, a product card, or a quick view — now or on a
  // later visit. A non-authoritative UX hint only (Klaviyo is the source of
  // truth), stored as { productId: expiryMs }: entries self-expire after the TTL,
  // and any storage failure fails open (no lock) rather than throwing.
  function saveSubscribed(map) {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch (e) {}
  }

  function loadSubscribed() {
    var map = {};
    try {
      var stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
      var now = Date.now();
      var pruned = false;
      Object.keys(stored).forEach(function (id) {
        if (stored[id] > now) map[id] = stored[id];
        else pruned = true;
      });
      if (pruned) saveSubscribed(map);
    } catch (e) { /* fail open */ }
    return map;
  }

  var subscribed = loadSubscribed();

  function isSubscribed(id) {
    return !!id && Object.prototype.hasOwnProperty.call(subscribed, String(id));
  }

  function rememberSubscribed(id) {
    if (!id || isSubscribed(id)) return;
    subscribed[String(id)] = Date.now() + STORAGE_TTL_MS;
    saveSubscribed(subscribed);
  }

  // Lock every notify trigger for an already-subscribed product so it can't
  // reopen the modal. Runs on load and whenever cards / quick views (re)render.
  function lockSubscribedTriggers() {
    if (!Object.keys(subscribed).length) return;
    Array.prototype.forEach.call(document.querySelectorAll('.js-klaviyo-restock'), function (btn) {
      if (btn.hasAttribute('data-klaviyo-restock-subscribed')) return;
      if (!isSubscribed(btn.getAttribute('data-product-id'))) return;
      btn.setAttribute('data-klaviyo-restock-subscribed', '');
      btn.setAttribute('disabled', 'disabled');
      btn.textContent = SUBSCRIBED_LABEL;
    });
  }

  /* ---- Modal ---- */

  function readProduct(btn) {
    return {
      id: btn.getAttribute('data-product-id') || '',
      title: btn.getAttribute('data-product-title') || '',
      handle: btn.getAttribute('data-product-handle') || ''
    };
  }

  function setMessage(text, kind) {
    if (!messageEl) return;
    messageEl.textContent = text || '';
    messageEl.setAttribute('data-state', kind || '');
    if (text) {
      messageEl.removeAttribute('hidden');
    } else {
      messageEl.setAttribute('hidden', '');
    }
  }

  function openModal(product) {
    activeProduct = product;
    lastFocused = document.activeElement;
    if (productSlot) {
      productSlot.textContent = product.title || 'this product';
    }
    setMessage('', '');
    if (form) form.reset();
    if (submitBtn) {
      submitBtn.textContent = LABEL_SHORT.split('[product]').join(product.title || '');
      submitBtn.removeAttribute('disabled');
      submitBtn.removeAttribute('inert');
    }
    modal.removeAttribute('hidden');
    document.documentElement.classList.add('klaviyo-restock-open');
    if (emailInput) {
      window.setTimeout(function () { emailInput.focus(); }, 0);
    }
  }

  function closeModal() {
    modal.setAttribute('hidden', '');
    document.documentElement.classList.remove('klaviyo-restock-open');
    activeProduct = null;
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  function isOpen() {
    return !modal.hasAttribute('hidden');
  }

  // Keep Tab focus inside the open modal: wrap at both ends, and pull focus back
  // in if it escapes. Self-contained (only acts while this modal is open) so it
  // can't conflict with the theme's own quick-view trap. Skips disabled/inert/
  // hidden controls (e.g. the submit button once it's locked after success).
  function trapFocus(e) {
    if (e.key !== 'Tab' || !isOpen()) return;
    var focusable = Array.prototype.filter.call(
      modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]'),
      function (el) {
        return !el.disabled
          && el.getAttribute('tabindex') !== '-1'
          && !el.closest('[inert]')
          && el.offsetParent !== null;
      }
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (!modal.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---- Klaviyo client-side API ---- */

  function subscribeToList(email) {
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: SOURCE,
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: LIST_ID } } }
      }
    };
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(SITE_ID), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: API_REVISION },
      body: JSON.stringify(body)
    });
  }

  function trackRequest(email, product) {
    var body = {
      data: {
        type: 'event',
        attributes: {
          properties: {
            product_id: product.id,
            product_name: product.title,
            product_handle: product.handle
          },
          metric: { data: { type: 'metric', attributes: { name: METRIC } } },
          profile: { data: { type: 'profile', attributes: { email: email } } }
        }
      }
    };
    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(SITE_ID), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: API_REVISION },
      body: JSON.stringify(body)
    });
  }

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!activeProduct) return;
    var email = (emailInput.value || '').trim();
    if (!validEmail(email)) {
      setMessage('Please enter a valid email address.', 'error');
      emailInput.focus();
      return;
    }
    if (consentInput && !consentInput.checked) {
      setMessage('Please check the box to consent.', 'error');
      consentInput.focus();
      return;
    }
    if (!SITE_ID) {
      setMessage('Sorry, notifications are unavailable right now. Please try again later.', 'error');
      return;
    }
    submitBtn.setAttribute('disabled', 'disabled');
    setMessage('Signing you up…', 'pending');

    var product = activeProduct;
    subscribeToList(email)
      .then(function (res) {
        if (!res.ok && res.status !== 202) throw new Error('subscribe failed: ' + res.status);
        // The subscription is the source of truth for "on the list", so confirm
        // and lock as soon as it succeeds. The product-tagging event is fired
        // best-effort afterwards — its failure must not make a successful signup
        // look failed (worst case: this signup is missing from the per-product
        // segment, but they are still subscribed).
        setMessage("We'll email you the moment it's available.", 'success');
        submitBtn.textContent = SUBSCRIBED_LABEL;
        submitBtn.removeAttribute('disabled');
        submitBtn.setAttribute('inert', '');
        rememberSubscribed(product.id);
        lockSubscribedTriggers();
        trackRequest(email, product).catch(function (err) {
          console.warn('klaviyo-restock: event failed (subscription succeeded)', err);
        });
      })
      .catch(function (err) {
        console.warn('klaviyo-restock: signup failed', err);
        setMessage('Something went wrong. Please try again in a moment.', 'error');
        submitBtn.removeAttribute('disabled');
      });
  }

  /* ---- Boost grid paused buttons ---- */

  // Boost rebuilds its grid cards in JS, so iw-purchase-paused.js (not Liquid)
  // gates them, tagging each paused buy button with data-purchase-paused. Turn
  // those into notify triggers, pulling the product name from the card title.
  function upgradePausedCards() {
    var buttons = document.querySelectorAll('[data-purchase-paused][data-prod][data-product-handle]');
    Array.prototype.forEach.call(buttons, function (btn) {
      if (btn.hasAttribute('data-klaviyo-restock-ready')) return;
      if (!btn.getAttribute('data-product-handle')) return;
      btn.setAttribute('data-klaviyo-restock-ready', '');
      btn.removeAttribute('disabled');
      // Drop the paused state's greyed look; data-purchase-paused stays so the
      // kill switch's gateCards leaves this button alone (CSS re-lifts opacity).
      btn.classList.remove('productCard__buy--oos');
      btn.classList.add('js-klaviyo-restock', 'klaviyo-restock-btn', 'btn--multiline');
      // handle + product-id already sit on the button (Boost mustache); only the
      // product name has to be lifted from the card title for the event/modal.
      var card = btn.closest('.boost-pfs-filter-product-item, .productCard, .product-card');
      var titleEl = card ? card.querySelector('.product-card__product-title, .productCard__title, h2 a, h3 a') : null;
      var title = titleEl ? titleEl.textContent.trim() : '';
      btn.setAttribute('data-product-title', title);
      btn.textContent = LABEL_SHORT.split('[product]').join(title);
    });
  }

  function watchForCards() {
    if (!window.MutationObserver) return;
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        upgradePausedCards();
        lockSubscribedTriggers();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ---- Init ---- */

  function onTriggerClick(e) {
    var trigger = e.target && e.target.closest ? e.target.closest('.js-klaviyo-restock') : null;
    if (!trigger) return;
    // A notify button keeps the .productCard__buy class for layout and is no
    // longer `disabled`, so the theme's delegated add-to-cart handler would also
    // fire. Capture phase + stopPropagation intercepts the click first and keeps
    // it from ever reaching that handler.
    e.preventDefault();
    e.stopPropagation();
    if (trigger.hasAttribute('data-klaviyo-restock-subscribed')) return;
    openModal(readProduct(trigger));
  }

  function init() {
    document.addEventListener('click', onTriggerClick, true);

    Array.prototype.forEach.call(modal.querySelectorAll('[data-klaviyo-restock-close]'), function (el) {
      el.addEventListener('click', closeModal);
    });
    // The tinted container is the backdrop now (no separate overlay element);
    // a click that lands on it — not inside the dialog — closes the modal.
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) closeModal();
    });
    document.addEventListener('keydown', trapFocus);
    if (form) form.addEventListener('submit', handleSubmit);

    upgradePausedCards();
    lockSubscribedTriggers();
    watchForCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
